import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { Session } from '@supabase/supabase-js'
import { supabaseAuthAdapter } from '@/adapters/auth'
import { api } from '@/adapters/api'
import type { LegalDocument, OnboardingDocuments, OnboardingStatus, Page, Viewer } from '@/contracts/api'

export const useAuthStore = defineStore('auth', () => {
  const session = ref<Session | null>(null)
  const viewer = ref<Viewer | null>(null)
  const onboardingStatus = ref<OnboardingStatus | null>(null)
  const onboardingDocuments = ref<OnboardingDocuments | null>(null)
  const ready = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  let unsubscribe: (() => void) | undefined

  const authenticated = computed(() => Boolean(session.value?.access_token))
  const onboarded = computed(
    () =>
      onboardingStatus.value?.requires_acceptance === false
      || onboardingStatus.value?.accepted === true,
  )

  async function loadOnboarding() {
    onboardingStatus.value = await api.get<OnboardingStatus>('/v1/onboarding/status', { accountScoped: false })
    return onboardingStatus.value
  }

  async function loadDocuments() {
    const response = await api.get<OnboardingDocuments | LegalDocument[] | Page<LegalDocument>>(
      '/v1/onboarding/documents',
      { accountScoped: false },
    )
    if ('terms' in response && 'privacy' in response) {
      onboardingDocuments.value = response
    } else {
      const items = Array.isArray(response) ? response : response.items
      const terms = items.find((document) => document.document_type === 'TERMS')
      const privacy = items.find((document) => document.document_type === 'PRIVACY')
      if (!terms || !privacy) throw new Error('The server did not return active Terms and Privacy documents.')
      onboardingDocuments.value = { terms, privacy }
    }
    return onboardingDocuments.value
  }

  async function loadViewer() {
    if (!session.value) {
      viewer.value = null
      return
    }
    const status = await loadOnboarding()
    if (status.requires_acceptance) {
      viewer.value = null
      return
    }
    viewer.value = await api.get<Viewer>('/v1/me', { accountScoped: false })
  }

  async function initialise() {
    if (ready.value) return
    try {
      session.value = await supabaseAuthAdapter.getSession()
      if (session.value) await loadViewer()
      unsubscribe = supabaseAuthAdapter.onSessionChange((next) => {
        session.value = next
        if (!next) viewer.value = null
        else void loadViewer()
      })
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : 'Authentication could not be initialised.'
    } finally {
      ready.value = true
    }
  }

  async function sendMagicLink(email: string) {
    loading.value = true
    error.value = null
    try {
      await supabaseAuthAdapter.sendMagicLink(email, `${window.location.origin}/auth/callback`)
    } finally {
      loading.value = false
    }
  }

  async function verifyOtp(email: string, token: string) {
    loading.value = true
    error.value = null
    try {
      session.value = await supabaseAuthAdapter.verifyOtp(email, token)
      await loadViewer()
    } finally {
      loading.value = false
    }
  }

  async function refreshToken() {
    session.value = await supabaseAuthAdapter.refreshSession()
    return session.value?.access_token ?? null
  }

  async function acceptTerms() {
    const documents = onboardingDocuments.value ?? await loadDocuments()
    await api.post<void>('/v1/onboarding/accept', {
      terms_version_id: documents.terms.id,
      privacy_version_id: documents.privacy.id,
      confirmed_18_plus: true,
    }, { accountScoped: false })
    await loadViewer()
  }

  async function signOut() {
    ready.value = false
    unsubscribe?.()
    unsubscribe = undefined
    await supabaseAuthAdapter.signOut()
    session.value = null
    viewer.value = null
    onboardingStatus.value = null
    onboardingDocuments.value = null
    localStorage.removeItem('marketal.account')
    const { useJobsStore } = await import('./jobs')
    const { useFilterStore } = await import('./filters')
    useJobsStore().reset()
    useFilterStore().reset()
  }

  return {
    session,
    viewer,
    onboardingStatus,
    onboardingDocuments,
    ready,
    loading,
    error,
    authenticated,
    onboarded,
    initialise,
    loadOnboarding,
    loadDocuments,
    loadViewer,
    sendMagicLink,
    verifyOtp,
    refreshToken,
    acceptTerms,
    signOut,
  }
})
